import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Plus, Shield, LogOut } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { collectionGroup, query, where, getDocs, getDoc, doc } from 'firebase/firestore'
import { getFirebaseDb } from '@/lib/firebase'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface Organization {
  id: string
  name: string
  slug: string
}

export function OrganizationsPage() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user) return

      try {
        const db = getFirebaseDb()
        
        // Check if user is super admin via custom claims
        const tokenResult = await user.getIdTokenResult()
        const isSuperAdmin = tokenResult.claims.is_super_admin === true
        setIsAdmin(isSuperAdmin)

        // Get user's organizations via members subcollection using collectionGroup query
        // This queries all 'members' subcollections across all organizations
        // Only members with userId set are actual members (created via Cloud Function when invite accepted)
        const membersGroup = collectionGroup(db, 'members')
        const memberQuery = query(
          membersGroup, 
          where('userId', '==', user.uid)
        )
        const membershipsSnapshot = await getDocs(memberQuery)
        
        // Fetch each organization the user is a member of
        // The parent path tells us which organization this membership belongs to
        const orgs: Organization[] = []
        for (const memberDoc of membershipsSnapshot.docs) {
          // Path is: organizations/{orgId}/members/{memberId}
          const orgId = memberDoc.ref.parent.parent?.id
          if (!orgId) continue
          
          const orgDoc = await getDoc(doc(db, 'organizations', orgId))
          if (orgDoc.exists()) {
            orgs.push({
              id: orgDoc.id,
              ...orgDoc.data()
            } as Organization)
          }
        }

        setOrganizations(orgs)

        // If user has only one org, redirect to it
        if (orgs.length === 1) {
          navigate(`/${orgs[0].slug}/assist`, { replace: true })
        }
      } catch (error) {
        console.error('Error fetching organizations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrganizations()
  }, [user, navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-4xl px-4 flex h-16 items-center justify-between">
          <div className="font-semibold text-lg">Session</div>
          <div className="flex items-center space-x-2">
            {isAdmin && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Super Admin
                </Link>
              </Button>
            )}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.photoURL || ''} alt={user.displayName || ''} />
                      <AvatarFallback>
                        {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.displayName}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin">
                          <Shield className="mr-2 h-4 w-4" />
                          Super Admin Panel
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Your Organizations</h1>
          <p className="text-gray-600 dark:text-gray-400">Select an organization to manage or create a new one</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
          {organizations.map((org) => (
            <Link key={org.id} to={`/${org.slug}/assist`}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardHeader>
                  <CardTitle>{org.name}</CardTitle>
                  <CardDescription>@{org.slug}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-end">
                    <Button variant="ghost" size="sm">
                      Open →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}

          {isAdmin && (
            <Link to="/organizations/new">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full border-dashed border-2 flex items-center justify-center">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Plus className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 font-medium">Create Organization</p>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        {organizations.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You're not part of any organizations yet.
              </p>
              {isAdmin ? (
                <div className="space-y-4">
                  <Button asChild>
                    <Link to="/organizations/new">
                      <Plus className="mr-2 h-4 w-4" />
                      Create Your First Organization
                    </Link>
                  </Button>
                  <p className="text-sm text-gray-500">
                    Or go to the <Link to="/admin" className="text-primary hover:underline">Super Admin Panel</Link> to manage all organizations.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Please contact a super admin to be added to an organization.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
